import { isEqual, isPlainObject } from "lodash-es";

import {
  TableBlockMultiColumnRecord,
  TableBlockSingleRecord,
} from "src/cases/queries/casesTypes";

/** One row of the diff. A row present on both sides was edited (or left
 *  untouched); a missing side means the row was added or removed. */
export type TableRowChange = {
  before: TableBlockSingleRecord | null;
  after: TableBlockSingleRecord | null;
};

export type TableRowChangeKind = "added" | "removed" | "edited";

const matchedRow = (
  before: TableBlockSingleRecord,
  after: TableBlockSingleRecord,
): TableRowChange => ({ before, after });

const addedRow = (after: TableBlockSingleRecord): TableRowChange => ({
  before: null,
  after,
});

const removedRow = (before: TableBlockSingleRecord): TableRowChange => ({
  before,
  after: null,
});

export const getRowChangeKind = (
  change: TableRowChange,
): TableRowChangeKind => {
  if (change.before === null) return "added";
  if (change.after === null) return "removed";
  return "edited";
};

export const isCellChanged = (change: TableRowChange, key: string): boolean =>
  !isEqual(change.before?.[key], change.after?.[key]);

/** Number of changes in a diff: an added or removed row counts as one change,
 *  an edited row counts one change per changed cell. */
export const countRecordChanges = (changes: TableRowChange[]): number =>
  changes.reduce((count, change) => {
    const kind = getRowChangeKind(change);
    if (kind === "added" || kind === "removed") return count + 1;

    const keys = new Set([
      ...Object.keys(change.before ?? {}),
      ...Object.keys(change.after ?? {}),
    ]);
    return count + [...keys].filter((key) => isCellChanged(change, key)).length;
  }, 0);

/** History events store the record as it is persisted: a single object for
 *  single-record tables, an array for multi-column ones, or null. */
export const normalizeTableRecord = (
  record: unknown,
): TableBlockMultiColumnRecord => {
  if (Array.isArray(record)) return record as TableBlockMultiColumnRecord;
  if (isPlainObject(record)) return [record as TableBlockSingleRecord];
  return [];
};

/** A persisted record with its shape: a single object (the block renders a
 *  key/value list) or a table of rows. */
export type TableRecordShape =
  | { kind: "single"; record: TableBlockSingleRecord }
  | { kind: "multi"; record: TableBlockMultiColumnRecord };

const toRecordShape = (record: unknown): TableRecordShape | null => {
  if (Array.isArray(record)) {
    return { kind: "multi", record: record as TableBlockMultiColumnRecord };
  }
  if (isPlainObject(record)) {
    return { kind: "single", record: record as TableBlockSingleRecord };
  }
  return null;
};

/** How a table edit is diffed: by property between two single records, by
 *  row between two tables, and as a removed-then-added pair of snapshots
 *  when the edit changed the shape itself (a table became a single record
 *  or the other way round). A missing side takes the other side's shape. */
export type TableRecordDiff =
  | {
      kind: "single";
      before: TableBlockSingleRecord;
      after: TableBlockSingleRecord;
    }
  | {
      kind: "multi";
      before: TableBlockMultiColumnRecord;
      after: TableBlockMultiColumnRecord;
    }
  | { kind: "reshaped"; before: TableRecordShape; after: TableRecordShape };

export const resolveTableRecordDiff = (
  beforeRecord: unknown,
  afterRecord: unknown,
): TableRecordDiff => {
  const before = toRecordShape(beforeRecord);
  const after = toRecordShape(afterRecord);

  if (before && after && before.kind !== after.kind) {
    return { kind: "reshaped", before, after };
  }
  // Here at most one shape is present, or both are the same kind.
  if (after?.kind === "single" || before?.kind === "single") {
    return {
      kind: "single",
      before: before?.kind === "single" ? before.record : {},
      after: after?.kind === "single" ? after.record : {},
    };
  }
  return {
    kind: "multi",
    before: normalizeTableRecord(beforeRecord),
    after: normalizeTableRecord(afterRecord),
  };
};

/** Items a snapshot is made of: a table's rows, or a single record's
 *  properties. */
const countShapeItems = (shape: TableRecordShape): number =>
  shape.kind === "multi"
    ? shape.record.length
    : Object.keys(shape.record).length;

export const countTableRecordChanges = (diff: TableRecordDiff): number => {
  switch (diff.kind) {
    case "single":
      return countRecordChanges([matchedRow(diff.before, diff.after)]);
    case "multi":
      return countRecordChanges(alignTableRecords(diff.before, diff.after));
    case "reshaped":
      // Everything before was removed and everything after was added.
      return countShapeItems(diff.before) + countShapeItems(diff.after);
  }
};

// Rows sharing at least half of their cells are shown as an in-place edit;
// anything less similar reads better as a removed row plus an added row.
const ROW_SIMILARITY_THRESHOLD = 0.5;

/** Share of cells that are equal across both rows, from 0 to 1. */
const rowSimilarity = (
  before: TableBlockSingleRecord,
  after: TableBlockSingleRecord,
): number => {
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  );
  if (keys.length === 0) return 1;
  const equalCells = keys.filter((key) => isEqual(before[key], after[key]));
  return equalCells.length / keys.length;
};

/** Longest-common-subsequence table over deep-equal rows, where
 *  `lengths[i][j]` is the longest common row subsequence of `before[i:]`
 *  and `after[j:]`. Built from the end so each row only needs the next one. */
const lcsLengths = (
  before: TableBlockMultiColumnRecord,
  after: TableBlockMultiColumnRecord,
): number[][] => {
  const emptyTail = Array<number>(after.length + 1).fill(0);

  return before.reduceRight<number[][]>(
    (rows, beforeRow) => {
      const [nextRow] = rows;
      const row = after.reduceRight<number[]>(
        (tail, afterRow, j) => [
          isEqual(beforeRow, afterRow)
            ? nextRow[j + 1] + 1
            : Math.max(tail[0], nextRow[j]),
          ...tail,
        ],
        [0],
      );
      return [row, ...rows];
    },
    [emptyTail],
  );
};

/**
 * Aligns the before/after row lists like a text diff. Deep-equal rows anchor
 * the alignment; between anchors, rows are paired up as in-place edits when
 * they are similar enough, and become removed/added rows otherwise. Row order
 * follows the after record, with removed rows kept at their original spot.
 */
export const alignTableRecords = (
  before: TableBlockMultiColumnRecord,
  after: TableBlockMultiColumnRecord,
): TableRowChange[] => {
  const lengths = lcsLengths(before, after);

  /** Whether both sides describe the same row — identical, or close enough
   *  to read as an edit without consuming a row that a later deep-equal
   *  match needs as its anchor. */
  const isSameRow = (i: number, j: number): boolean =>
    isEqual(before[i], after[j]) ||
    (lengths[i + 1][j + 1] === lengths[i][j] &&
      rowSimilarity(before[i], after[j]) >= ROW_SIMILARITY_THRESHOLD);

  const alignFrom = (i: number, j: number): TableRowChange[] => {
    if (i === before.length && j === after.length) return [];
    if (i === before.length) {
      return [addedRow(after[j]), ...alignFrom(i, j + 1)];
    }
    if (j === after.length) {
      return [removedRow(before[i]), ...alignFrom(i + 1, j)];
    }
    if (isSameRow(i, j)) {
      return [matchedRow(before[i], after[j]), ...alignFrom(i + 1, j + 1)];
    }
    // Otherwise skip the side that keeps the most deep-equal rows aligned.
    return lengths[i + 1][j] >= lengths[i][j + 1]
      ? [removedRow(before[i]), ...alignFrom(i + 1, j)]
      : [addedRow(after[j]), ...alignFrom(i, j + 1)];
  };

  return alignFrom(0, 0);
};
